import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { resetPassword } from '../../services/loyaltyAuthApi';
import { FONT_FAMILY } from '../../theme';

export default function ResetPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const tokenParam = searchParams.get('token');
        const emailParam = searchParams.get('email');

        if (!tokenParam || !emailParam) {
            setError(t('loyalty.resetPassword.invalidLink') || 'Invalid or missing reset link parameters');
        } else {
            setToken(tokenParam);
            setEmail(emailParam);
        }
    }, [searchParams, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!password || !passwordConfirmation) {
            setError(t('loyalty.auth.errors.fillAllFields') || 'Please fill all fields');
            return;
        }

        if (password !== passwordConfirmation) {
            setError(t('loyalty.auth.errors.passwordMismatch') || 'Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError(t('loyalty.auth.errors.passwordTooShort') || 'Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            await resetPassword({
                email,
                token,
                password,
                password_confirmation: passwordConfirmation
            });

            setSuccess(true);

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/loyalty/login', {
                    state: { message: t('loyalty.resetPassword.successRedirect') || 'Password reset successful! Please login with your new password.' }
                });
            }, 3000);
        } catch (err) {
            if (err.errors) {
                // Handle validation errors
                const errorMessages = Object.values(err.errors).flat();
                const translatedErrors = errorMessages.map(msg => {
                    if (msg.includes('confirmation does not match')) {
                        return t('loyalty.resetPassword.errors.passwordMismatch') || msg;
                    } else if (msg.includes('at least 8 characters')) {
                        return t('loyalty.resetPassword.errors.passwordTooShort') || msg;
                    } else if (msg.includes('required')) {
                        return t('loyalty.resetPassword.errors.fieldRequired') || msg;
                    }
                    return msg;
                });
                setError(translatedErrors.join(' '));
            } else {
                // Translate common backend error messages
                const errorMsg = err.message || '';
                if (errorMsg.includes('Invalid password reset token')) {
                    setError(t('loyalty.resetPassword.errors.invalidToken') || errorMsg);
                } else if (errorMsg.includes('expired')) {
                    setError(t('loyalty.resetPassword.errors.tokenExpired') || errorMsg);
                } else {
                    setError(errorMsg || t('loyalty.auth.errors.genericError') || 'An error occurred');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#1a1a1a',
                    backgroundImage: 'url(/images/gallery-images/20231106_183210-scaled.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.75)',
                        backdropFilter: 'blur(3px)',
                    },
                }}
            >
                <Card
                    sx={{
                        maxWidth: 480,
                        width: '100%',
                        mx: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.98)',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                        borderRadius: 3,
                        position: 'relative',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />

                        <Typography
                            variant="h4"
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                fontFamily: FONT_FAMILY,
                                color: '#111',
                            }}
                        >
                            {t('loyalty.resetPassword.successTitle') || 'Password Reset Successful!'}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}
                        >
                            {t('loyalty.resetPassword.successMessage') || 'Your password has been reset successfully. You can now login with your new password.'}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ color: '#999', fontStyle: 'italic' }}
                        >
                            {t('loyalty.resetPassword.redirecting') || 'Redirecting to login page...'}
                        </Typography>

                        <CircularProgress size={24} sx={{ mt: 2, color: '#ed5034' }} />
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1a1a1a',
                backgroundImage: 'url(/images/gallery-images/20231106_183210-scaled.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(3px)',
                },
            }}
        >
            <Card
                sx={{
                    maxWidth: 480,
                    width: '100%',
                    mx: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.98)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    borderRadius: 3,
                    position: 'relative',
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                    <Typography
                        variant="h4"
                        sx={{
                            mb: 1,
                            fontWeight: 700,
                            fontFamily: FONT_FAMILY,
                            color: '#111',
                            textAlign: 'center',
                        }}
                    >
                        {t('loyalty.resetPassword.title') || 'Reset Password'}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mb: 4,
                            color: '#666',
                            textAlign: 'center',
                            lineHeight: 1.6,
                        }}
                    >
                        {t('loyalty.resetPassword.description') || 'Enter your new password below.'}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            label={t('loyalty.auth.email') || 'Email'}
                            type="email"
                            value={email}
                            fullWidth
                            disabled
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            label={t('loyalty.auth.newPassword') || 'New Password'}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            required
                            disabled={loading || !token}
                            sx={{ mb: 2 }}
                            autoFocus
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label={t('loyalty.auth.confirmPassword') || 'Confirm New Password'}
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            fullWidth
                            required
                            disabled={loading || !token}
                            sx={{ mb: 3 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || !token}
                            sx={{
                                bgcolor: '#ed5034',
                                color: '#fff',
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#d64528' },
                                '&:disabled': { bgcolor: '#ccc' },
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: '#fff' }} />
                            ) : (
                                t('loyalty.resetPassword.submitButton') || 'Reset Password'
                            )}
                        </Button>
                    </form>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            {t('loyalty.resetPassword.backToLogin') || 'Back to'}{' '}
                            <Link
                                to="/loyalty/login"
                                style={{
                                    color: '#ed5034',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                {t('loyalty.auth.login') || 'Login'}
                            </Link>
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
